import {
  useRef,
  useEffect,
} from 'react';
import type {
  ReactNode,
} from 'react';
import classNames from 'classnames';
import isBrowser from '@/utils/isBrowser';

import styles from './InfiniteScroll.module.css';

type InfiniteScrollProps = {
  children?: ReactNode;
  hasMore: boolean;
  loading?: boolean;
  threshold: number;
  className?: string;
  loadMore: () => Promise<void>;
}

const InfiniteScroll = (props: InfiniteScrollProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    children,
    hasMore,
    loading,
    threshold,
    className,
    loadMore,
  } = props;

  // ##########################################################################
  // STATE
  // ##########################################################################

  const scrollableRef = useRef<HTMLDivElement>(null);

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const withRef = !isBrowser || children;
  const scrollable = withRef
    ? scrollableRef.current
    : window;
  const scrollPosition = withRef
    ? scrollableRef.current
    : document.documentElement;

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const onScroll = () => {
    if (
      loading ||
      !scrollable
    ) return;

    const {
      scrollTop,
      scrollHeight,
      clientHeight,
    } = scrollPosition;
    const position = scrollHeight - scrollTop - clientHeight;

    if (
      !hasMore ||
      position > threshold
    ) return;

    scrollable.onscroll = null;
    loadMore();
  };

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useEffect(() => {
    if (
      !scrollable
    ) return;

    scrollable.onscroll = null;

    if (
      loading ||
      !hasMore
    ) return;

    scrollable.onscroll = onScroll;
  }, [loading, hasMore, onScroll]);

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const infiniteScrollClassName = classNames({
    [styles.infiniteScroll]: true,
    [className]: !!className,
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  if (
    !children
  ) return null;

  return (
    <div
      ref={scrollableRef}
      className={infiniteScrollClassName}
    >
      {children}
    </div>
  );
};

export default InfiniteScroll;
